<script setup lang="ts">
import { ref } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import { useMediaQuery } from '@vueuse/core';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import LzpCommandList from './LzpCommandList.vue';
import type { CommandEnumOption } from '@/composables/api/useLocationZonePlayerInspectorApi';

/**
 * The command chooser for the bulk-issue bar. The list is searchable either way
 * (see {@link LzpCommandList}); the chrome is responsive: a tall anchored popover
 * on desktop (plenty of vertical room for the catalogue), a full-screen modal on
 * mobile where an anchored dropdown would be cramped.
 */
defineProps<{
    commands: CommandEnumOption[];
    loading?: boolean;
    selected?: CommandEnumOption | null;
    /** Per-option picker gating for the host's targets (see useIssueCommandForm). Absent → no gating. */
    optionGateReason?: (command: CommandEnumOption) => string | null;
}>();

const emit = defineEmits<{ (e: 'select', command: CommandEnumOption): void }>();

const open = ref(false);
const isDesktop = useMediaQuery('(min-width: 768px)');

function onSelect(command: CommandEnumOption): void {
    emit('select', command);
    open.value = false;
}
</script>

<template>
    <!-- Desktop: anchored popover, tall + scrollable. -->
    <Popover v-if="isDesktop" v-model:open="open">
        <PopoverTrigger as-child>
            <button
                type="button"
                class="border-border/60 hover:bg-muted/40 text-foreground inline-flex max-w-56 items-center gap-1.5 truncate rounded-md border px-2 py-0.5"
                data-testid="lzp-bulk-command-trigger"
            >
                <span class="truncate" :class="selected ? '' : 'text-muted-foreground'">
                    {{ selected?.display_string ?? 'a command…' }}
                </span>
                <ChevronDownIcon class="size-3.5 shrink-0" aria-hidden="true" />
            </button>
        </PopoverTrigger>
        <!--
            `flex flex-col` matters: without it the list sat at its natural
            height inside an auto-height popover, so the inner `overflow-y-auto`
            never had anything to scroll — `max-h` just clipped the overflow and
            everything past the cut was unreachable. As a flex column the popover
            takes its max height once the catalogue is taller, and the list
            (`min-h-0 flex-1`) shrinks into it and scrolls, same as mobile.
        -->
        <PopoverContent align="start" class="flex max-h-[min(70vh,40rem)] w-80 flex-col overflow-hidden p-0">
            <LzpCommandList
                class="min-h-0 flex-1"
                :commands="commands"
                :loading="loading"
                :selected="selected"
                :option-gate-reason="optionGateReason"
                @select="onSelect"
            />
        </PopoverContent>
    </Popover>

    <!-- Mobile: full-screen modal. -->
    <Dialog v-else v-model:open="open">
        <DialogTrigger as-child>
            <button
                type="button"
                class="border-border/60 hover:bg-muted/40 text-foreground inline-flex max-w-56 items-center gap-1.5 truncate rounded-md border px-2 py-0.5"
                data-testid="lzp-bulk-command-trigger"
            >
                <span class="truncate" :class="selected ? '' : 'text-muted-foreground'">
                    {{ selected?.display_string ?? 'a command…' }}
                </span>
                <ChevronDownIcon class="size-3.5 shrink-0" aria-hidden="true" />
            </button>
        </DialogTrigger>
        <!--
            The overlay has to stop above the player too, not just the content.
            Its default is `fixed inset-0 bg-black/80`, so shrinking only the
            content left the bar sitting under a black scrim — visible at 20%
            and, because reka locks `pointer-events` on <body> for a modal
            dialog, not tappable either. That is most of the way back to "no way
            to see or pause it". The player opts out of the body lock with its
            own `pointer-events-auto` (see SidebarLayout).
        -->
        <DialogContent
            overlay-class="inset-x-0 top-0 bottom-[var(--global-audio-player-height,0px)]"
            class="top-0 left-0 flex h-[calc(100dvh-var(--global-audio-player-height,0px))] max-h-[calc(100dvh-var(--global-audio-player-height,0px))] w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0"
        >
            <DialogTitle class="border-border/60 border-b px-4 py-3 text-sm font-semibold">Choose a command</DialogTitle>
            <LzpCommandList
                class="min-h-0 flex-1"
                :commands="commands"
                :loading="loading"
                :selected="selected"
                :option-gate-reason="optionGateReason"
                @select="onSelect"
            />
        </DialogContent>
    </Dialog>
</template>
